import React, { createRef } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Card } from '@blueprintjs/core'
import { Box } from 'reflexbox'
import isEqual from 'lodash/isEqual'

import ActionsList from './ActionsList'
import ActionDetailsDrawer from './ActionDetailsDrawer'
import ActivityLogs from './ActivityLogs'
import HistoryLogs from './HistoryLogs'
import DecisionLogs from './DecisionLogs'
import IssueLogs from './IssueLogs'
import FilterControls from './Controls/FilterControls'
import Header from './Header'
import ManageControls from './Controls/ManageControls'
import Dialogs from './Controls/Dialogs'
import DataPlaceholder from 'src/components/DataPlaceholder'
import Pagination from 'src/utils/pagination'
import * as runbookModules from 'src/store/modules/runbooks'
import * as actionTypes from 'src/constants/actionTypes'
import {
    ACTION_COMMENT_LOG_TYPE,
    ACTION_HISTORY_LOG_TYPE,
} from 'src/constants/actionLogTypes'
import { getRunbook, getRunbookFromServer } from 'src/store/modules/runbooks'
import { setActiveRunbook } from 'src/store/modules/ui/runbook'
import noActionsImage from 'src/assets/icons/no-actions.svg'
import { LOGS } from 'src/routes/Runbook/Controls/FilterControls'

import styles from './runbook.module.scss'
import { replace } from 'connected-react-router'
import {
    getVisibleActionUids,
    getVisibleOrphans,
} from 'src/store/modules/runbooks/actions'

const isNew = (id) => id === 'new'
const getId = (id) => (isNew(id) ? null : parseInt(id, 10))

// represents the height of the navbar and the margin/padding between
// the navbar and the runbooks controls
const ADDITIONAL_HEIGHT = 216

class Runbook extends React.Component {
    state = {
        topContainerHeight: 0,
        forecastDate: null,
        forecastCalculating: false,
        currentMatch: null,
        findCriteria: {},
        matchedRows: [],
    }
    listRef = createRef()

    topContainerRef = createRef()

    componentDidMount() {
        const { match, runbookId, actions } = this.props

        // The header/filters can wrap on narrower screens, re-render so the
        // actions list height is recalculated (same listener is not added twice)
        window.addEventListener('resize', this.handleWindowResize)

        if (runbookId) {
            actions.getRunbookFromServer(match.params.id)
        } else {
            actions.setActiveRunbook(runbookId)
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize)
        cancelAnimationFrame(this.resizeFrame)
    }

    handleWindowResize = () => {
        cancelAnimationFrame(this.resizeFrame)
        this.resizeFrame = requestAnimationFrame(() => this.forceUpdate())
    }

    componentDidUpdate(prevProps) {
        const { topContainerHeight } = this.state
        const {
            runbook,
            runbookId,
            actions,
            dispatch,
            location,
            selectedTemplateActions,
            loadingTemplateActions,
        } = this.props

        // Hydrate from template if we're on a new runbook and have template actions
        if (
            runbook?.templateId &&
            runbook.actions.uids.length === 0 &&
            !loadingTemplateActions &&
            (prevProps.loadingTemplateActions ||
                (selectedTemplateActions.length > 0 &&
                    prevProps.selectedTemplateActions.length === 0))
        ) {
            actions.applyTemplateActions(runbookId, selectedTemplateActions)
        }

        // Redirect to home if user go directly to /new path without going through the create runbook modal
        if (
            !runbook?.organization &&
            location.pathname.includes('/runbooks/new')
        ) {
            dispatch(replace('/'))
        }
        if (runbook && runbook.name) {
            document.title = `${runbook.name} | Runbooks`
        }

        if (
            this.topContainerRef.current &&
            topContainerHeight !==
                this.topContainerRef.current.offsetHeight + ADDITIONAL_HEIGHT
        ) {
            this.setState({
                topContainerHeight:
                    this.topContainerRef.current.offsetHeight +
                    ADDITIONAL_HEIGHT,
            })
        }

        if (runbookId && prevProps.runbookId !== runbookId) {
            actions.getRunbookFromServer(runbookId)
        }

        if (
            !isEqual(this.props.actionUids, prevProps.actionUids) ||
            !isEqual(this.props.orphanedUids, prevProps.orphanedUids)
        ) {
            this.applyFilters()
        }
    }

    applyFilters = () => {
        const { findCriteria } = this.state
        const { searchFilter = '', ownerFilter = '' } = findCriteria
        const itemData = this.getItemData()

        const matchedRows = itemData.reduce((acc, item, idx) => {
            if (item === 'buffer' || (!searchFilter && !ownerFilter)) {
                return acc
            }
            const action = this.props.actionsById[item]
            const isFiltered =
                action?.filteredByAction ||
                action?.filteredByUser ||
                !action?.visible

            if (!action || isFiltered) {
                return acc
            }

            const nameMatches = searchFilter
                ? action.name
                      ?.toLowerCase()
                      .includes(searchFilter.toLowerCase())
                : true

            const ownerTeam = (action.ownerTeam || '').toLowerCase()
            const ownerUser = (action.ownerUser || '').toLowerCase()
            const ownerMatches = ownerFilter
                ? ownerTeam.includes(ownerFilter.toLowerCase()) ||
                  ownerUser.includes(ownerFilter.toLowerCase())
                : true

            if (nameMatches && ownerMatches) {
                acc.push(idx)
            }
            return acc
        }, [])

        this.setState({ matchedRows, currentMatch: 0 })
    }

    handleSearchFilter = (searchText) => {
        this.setState(
            (prev) => ({
                findCriteria: {
                    ...prev?.findCriteria,
                    searchFilter: searchText,
                },
            }),
            this.applyFilters
        )
    }

    handleSearchOwner = (searchText) => {
        this.setState(
            (prev) => ({
                findCriteria: {
                    ...prev?.findCriteria,
                    ownerFilter: searchText,
                },
            }),
            this.applyFilters
        )
    }

    goToNextMatch = () => {
        const { currentMatch, matchedRows } = this.state
        if (matchedRows && matchedRows.length > 0) {
            const newMatch = (currentMatch + 1) % matchedRows.length
            this.setState({ currentMatch: newMatch })
            if (this.listRef.current) {
                const rowIndex = matchedRows[newMatch]
                this.listRef.current.scrollToItem(rowIndex, 'center')
            }
        }
    }

    goToPreviousMatch = () => {
        const { currentMatch, matchedRows } = this.state
        if (matchedRows && matchedRows.length > 0) {
            const newMatch =
                (currentMatch - 1 + matchedRows.length) % matchedRows.length
            this.setState({ currentMatch: newMatch })
            if (this.listRef.current) {
                const rowIndex = matchedRows[newMatch]
                this.listRef.current.scrollToItem(rowIndex, 'center')
            }
        }
    }

    resetCurrentMatch = () => {
        this.setState({ currentMatch: 0 })
    }

    getItemData() {
        const { actionUids, orphanedUids } = this.props
        let itemData = actionUids
        if (orphanedUids.length) {
            itemData = [...actionUids, 'buffer', ...orphanedUids]
        }

        return itemData
    }

    _renderActionsList() {
        const {
            runbook,
            runbookId,
            activeRunbook,
            activeAction,
            runbookLoading,
            loadingTemplateActions,
        } = this.props
        const topContainerHeight =
            (this.topContainerRef.current?.offsetHeight ?? 0) + 216

        const isLoading =
            runbookLoading || (runbook.templateId && loadingTemplateActions)

        return isLoading || runbook.actions.uids.length > 0 ? (
            <React.Fragment>
                <Box
                    style={{
                        height: `calc(100vh - ${topContainerHeight}px)`,
                    }}
                    w={1}
                >
                    {runbookId === activeRunbook && (
                        <ActionsList
                            currentMatch={this.state?.currentMatch ?? null}
                            listRef={this.listRef}
                            matchedRows={this.state?.matchedRows ?? []}
                        />
                    )}
                </Box>
                {activeAction && <ActionDetailsDrawer />}
            </React.Fragment>
        ) : (
            <DataPlaceholder
                title="No actions have been made in this Runbook yet."
                subtitle={
                    <>
                        Click at the right top corner on
                        <b>“Edit Runbook”</b> and click on the plus icon to add
                        an action.
                    </>
                }
                img={noActionsImage}
            />
        )
    }

    setCurrentPage = (newPage) => {
        const logTypes = {
            [LOGS.COMMENT]: {
                type: actionTypes.GET_ACTION_LOGS,
                action: () =>
                    this.props.actions.getAllHistoryLogsFromServer(
                        this.props.runbook.activeSession,
                        1,
                        newPage,
                        this.props.perPage
                    ),
            },
            [LOGS.HISTORY]: {
                type: actionTypes.GET_ALL_ACTION_LOGS,
                action: () =>
                    this.props.actions.getAllHistoryLogsFromServer(
                        this.props.runbook.activeSession,
                        0,
                        newPage,
                        this.props.perPage
                    ),
            },
            [LOGS.DECISIONS]: {
                type: actionTypes.GET_ALL_DECISION_LOGS,
                action: () =>
                    this.props.actions.getAllDecisionLogsFromServer(
                        this.props.runbook.activeSession,
                        newPage,
                        this.props.perPage
                    ),
            },
            [LOGS.ISSUE]: {
                type: actionTypes.GET_ALL_ISSUE_LOGS,
                action: () =>
                    this.props.actions.getAllIssueLogsFromServer(
                        this.props.runbook.activeSession,
                        newPage,
                        this.props.perPage
                    ),
            },
            [LOGS.ACTIVITIES]: {
                type: actionTypes.GET_ALL_ACTIVITY_LOGS,
                action: () =>
                    this.props.actions.getAllActivityLogsFromServer(
                        this.props.runbook.activeSession,
                        newPage,
                        this.props.perPage
                    ),
            },
        }
        const { dispatch, selectedWindow } = this.props

        dispatch({
            type: logTypes[selectedWindow],
            page: newPage,
        })
        logTypes[selectedWindow].action()
    }

    render() {
        const { runbook, activeRunbook, runbookId, selectedWindow } = this.props

        if (runbook === undefined) return null

        return (
            <div className={styles.root}>
                <div ref={this.topContainerRef}>
                    <Header
                        runbook={runbook}
                        onCancelEditing={() => this.componentDidMount()}
                        selectedWindow={selectedWindow}
                    />
                    {runbook.isEditing || runbook.activeSession ? (
                        <ManageControls runbook={runbook} />
                    ) : null}
                </div>
                <Card>
                    <FilterControls
                        listRef={this.listRef}
                        currentMatch={this.state?.currentMatch}
                        goToNextMatch={this.goToNextMatch}
                        goToPreviousMatch={this.goToPreviousMatch}
                        resetCurrentMatch={this.resetCurrentMatch}
                        handleSearch={this.handleSearchFilter}
                        handleSearchOwner={this.handleSearchOwner}
                        searchFilter={this.state?.findCriteria?.searchFilter}
                        ownerFilter={this.state?.findCriteria?.ownerFilter}
                        matchedRows={this.state?.matchedRows ?? []}
                    />
                    {selectedWindow === 'actions-list'
                        ? this._renderActionsList()
                        : null}

                    {selectedWindow === LOGS.HISTORY && (
                        <Box w={1}>
                            {runbookId === activeRunbook && (
                                <HistoryLogs
                                    session={runbook.activeSession}
                                    logType={ACTION_HISTORY_LOG_TYPE}
                                />
                            )}
                        </Box>
                    )}
                    {selectedWindow === LOGS.COMMENT && (
                        <Box w={1}>
                            {runbookId === activeRunbook && (
                                <HistoryLogs
                                    session={runbook.activeSession}
                                    logType={ACTION_COMMENT_LOG_TYPE}
                                />
                            )}
                        </Box>
                    )}
                    {selectedWindow === LOGS.DECISIONS && (
                        <Box w={1}>
                            {runbookId === activeRunbook && (
                                <DecisionLogs session={runbook.activeSession} />
                            )}
                        </Box>
                    )}
                    {selectedWindow === LOGS.ISSUE && (
                        <Box w={1}>
                            {runbookId === activeRunbook && (
                                <IssueLogs session={runbook.activeSession} />
                            )}
                        </Box>
                    )}
                    {selectedWindow === LOGS.ACTIVITIES && (
                        <Box w={1}>
                            {runbookId === activeRunbook && (
                                <ActivityLogs
                                    session={runbook.activeSession}
                                    currentMatch={
                                        this.state?.currentMatch ?? null
                                    }
                                />
                            )}
                        </Box>
                    )}
                    {selectedWindow !== 'actions-list' && (
                        <Pagination
                            currentPage={runbook.page}
                            totalElements={runbook.totalElements}
                            totalPages={runbook.totalPages}
                            perPage={runbook.perPage}
                            onPageChange={(newPage) =>
                                this.setCurrentPage(newPage)
                            }
                            renderPage={() => null}
                        />
                    )}
                </Card>
                <Dialogs />
            </div>
        )
    }
}

Runbook.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    }),
}

const mapStateToProps = (state, ownProps) => {
    const runbookId =
        getId(ownProps.match.params.id) || state.ui.runbook.activeRunbook

    const actionUids = getVisibleActionUids(
        state,
        state.ui.runbook.activeRunbook
    )
    const orphanedUids = getVisibleOrphans(
        state,
        state.ui.runbook.activeRunbook
    )

    const runbook = getRunbook(state, runbookId)
    const templatesState = state.runbooks.templates || {
        loadingSelectedTemplateActions: false,
        selectedTemplateActions: [],
    }

    return {
        runbook,
        runbookId,
        actionsById: state.runbooks.byId[runbookId]?.actions?.byId,
        activeRunbook: state.ui.runbook.activeRunbook,
        activeAction: state.ui.runbook.controls.singleOpenAction,
        selectedWindow: state.ui.runbook.controls.selectedWindow,
        runbookLoading: state.ui.runbook.runbookLoading,
        loadingTemplateActions: templatesState.loadingSelectedTemplateActions,
        selectedTemplateActions: templatesState.selectedTemplateActions,
        actionUids,
        orphanedUids,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                getRunbookFromServer,
                setActiveRunbook,
                getAllHistoryLogsFromServer:
                    runbookModules.getAllHistoryLogsFromServer,
                getAllIssueLogsFromServer:
                    runbookModules.getAllIssueLogsFromServer,
                getAllDecisionLogsFromServer:
                    runbookModules.getAllDecisionLogsFromServer,
                getAllActivityLogsFromServer:
                    runbookModules.getAllActivityLogsFromServer,
                applyTemplateActions: runbookModules.applyTemplateActions,
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Runbook)
